class ThemeMenu {
  constructor() {
    this.openButtons = [...document.querySelectorAll('header [data-header-menu], header [data-menu-open]')];
    this.openButton = this.openButtons[0];
    this.closeButton = document.querySelector('[data-menu-close]');
    this.backdrop = document.querySelector('[data-menu-backdrop]');
    this.drawer = document.querySelector('[data-mobile-drawer]');
    this.lastFocused = null;
    if (!this.drawer) return;

    this.openButtons.forEach((button) => button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.toggle(true, button.dataset.drawerTabTarget);
    }));
    this.closeButton?.addEventListener('click', () => this.toggle(false));
    this.backdrop?.addEventListener('click', () => this.toggle(false));
    this.drawer.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => this.toggle(false)));
    this.drawer.querySelectorAll('[data-drawer-tab]').forEach((tab) => tab.addEventListener('click', () => this.selectTab(tab.dataset.drawerTab)));
    document.addEventListener('keydown', (event) => this.handleKeydown(event));
  }

  selectTab(name) {
    this.drawer.querySelectorAll('[data-drawer-tab]').forEach((tab) => {
      const active = tab.dataset.drawerTab === name;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
    });
    this.drawer.querySelectorAll('[data-drawer-panel]').forEach((panel) => {
      const active = panel.dataset.drawerPanel === name;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
    });
  }

  handleKeydown(event) {
    if (!this.drawer.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      this.toggle(false);
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...this.drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), summary')].filter((element) => !element.closest('[hidden]'));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  toggle(open, tabName) {
    if (open) this.lastFocused = document.activeElement;
    if (open && tabName) this.selectTab(tabName);
    this.drawer.classList.toggle('is-open', open);
    this.drawer.setAttribute('aria-hidden', String(!open));
    this.drawer.inert = !open;
    this.openButton?.setAttribute('aria-expanded', String(open));
    document.body.classList.toggle('drawer-open', open);
    if (open) this.closeButton?.focus();
    else (this.lastFocused || this.openButton)?.focus();
  }
}

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('button').forEach((button) => button.addEventListener('click', () => {
      const input = this.querySelector('input');
      const step = Number(input.step || 1);
      const next = Number(input.value) + (button.name === 'plus' ? step : -step);
      input.value = Math.max(Number(input.min || 0), next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }));
  }
}
customElements.define('quantity-input', QuantityInput);

class FitProductSwitcher {
  constructor() {
    this.finder = document.querySelector('[data-fit-finder]');
    if (!this.finder) return;
    this.cards = [...this.finder.querySelectorAll('[data-fit-product-card]')];
    this.products = JSON.parse(this.finder.querySelector('[data-fit-products]')?.textContent || '[]').filter(Boolean);
    this.cards.forEach((card) => {
      card.addEventListener('click', () => this.select(card.dataset.productHandle));
    });
    const first = this.cards.find((card) => card.classList.contains('is-active')) || this.cards[0];
    if (first) this.select(first.dataset.productHandle, false);
  }

  select(handle) {
    if (!handle) return;
    const product = this.products.find((item) => item.handle === handle);
    if (!product) return;
    this.cards.forEach((card) => {
      const active = card.dataset.productHandle === handle;
      card.classList.toggle('is-active', active);
      const input = card.querySelector('input');
      if (input) input.checked = active;
    });
    this.finder.dataset.selectedHandle = handle;
    window.dispatchEvent(new CustomEvent('fit:product-change', { detail: product }));
  }
}

class SpotlightProductSwitcher {
  constructor() {
    this.spotlight = document.querySelector('[data-product-spotlight]');
    if (!this.spotlight) return;
    this.title = this.spotlight.querySelector('[data-spotlight-title]');
    this.price = this.spotlight.querySelector('[data-spotlight-price]');
    this.description = this.spotlight.querySelector('[data-spotlight-description]');
    this.form = this.spotlight.querySelector('.home-product__buy');
    this.gallery = this.spotlight.querySelector('[data-product-gallery]');
    this.onProductChange = (event) => this.update(event.detail);
    window.addEventListener('fit:product-change', this.onProductChange);
  }

  destroy() {
    window.removeEventListener('fit:product-change', this.onProductChange);
  }

  update(product) {
    if (!product) return;
    if (this.title) this.title.textContent = product.title || '';
    if (this.price) this.price.innerHTML = `<div class="price"><span>${this.escape(product.price || '')}</span></div>`;
    if (this.description) this.description.innerHTML = product.description ? `<p>${this.escape(product.description)}</p>` : '';
    this.updateGallery(product);
    this.updateForm(product);
  }

  updateGallery(product) {
    if (!this.gallery) return;
    const media = [...new Set([product.image, ...(product.media || [])].filter(Boolean))].slice(0, 8);
    if (!media.length) return;
    const main = this.gallery.querySelector('.home-product__main-media');
    if (!main) return;
    let image = main.querySelector('img');
    if (!image) {
      main.querySelector('.placeholder-svg')?.remove();
      image = document.createElement('img');
      main.prepend(image);
    }
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.src = media[0];
    image.alt = product.title || '';

    const thumbs = this.gallery.querySelector('.home-product__thumbs');
    if (thumbs) {
      thumbs.replaceChildren();
      media.forEach((url, index) => {
        const button = document.createElement('button');
        button.className = `media home-product__thumb${index === 0 ? ' is-active' : ''}`;
        button.type = 'button';
        button.dataset.galleryThumb = '';
        button.dataset.galleryImage = url;
        button.setAttribute('aria-label', `Show image ${index + 1}`);
        button.setAttribute('aria-current', String(index === 0));
        const thumbImage = document.createElement('img');
        thumbImage.src = url;
        thumbImage.alt = product.title || '';
        thumbImage.loading = 'lazy';
        button.append(thumbImage);
        thumbs.append(button);
      });
    }
    this.gallery.productGallery?.refresh();
  }

  updateForm(product) {
    if (!this.form) return;
    const variants = product.variants || [];
    const available = variants.find((variant) => variant.available) || variants[0];
    const idInput = this.form.querySelector('[data-variant-id]');
    const variantScript = this.form.querySelector('[data-product-variants]');
    if (idInput && available) idInput.value = available.id;
    if (variantScript) variantScript.textContent = JSON.stringify(variants);
    this.form.querySelectorAll('fieldset').forEach((fieldset) => fieldset.remove());
    const hiddenId = this.form.querySelector('[data-variant-id]');
    if (hiddenId && product.options?.length) {
      product.options.forEach((option, optionIndex) => {
        const fieldset = document.createElement('fieldset');
        const legend = document.createElement('legend');
        legend.innerHTML = `${this.escape(option.name)}: <strong>${this.escape(available?.options?.[optionIndex] || option.values?.[0] || '')}</strong>`;
        const values = document.createElement('div');
        values.className = 'option-values';
        (option.values || []).forEach((value, valueIndex) => {
          const id = `Spotlight-${optionIndex}-${valueIndex}`;
          const input = document.createElement('input');
          input.type = 'radio';
          input.id = id;
          input.name = `options[${option.name}]`;
          input.value = value;
          input.checked = available?.options?.[optionIndex] === value;
          const label = document.createElement('label');
          label.htmlFor = id;
          label.textContent = value;
          values.append(input, label);
        });
        fieldset.append(legend, values);
        this.form.insertBefore(fieldset, hiddenId);
      });
    }
    const submit = this.form.querySelector('[type="submit"]');
    if (submit) submit.disabled = !available || !available.available;
    if (this.form.dataset.initialized) delete this.form.dataset.initialized;
    new ProductForm(this.form);
  }

  escape(value) {
    const element = document.createElement('span');
    element.textContent = value;
    return element.innerHTML;
  }
}

class ProductForm {
  constructor(form) {
    this.form = form;
    this.variants = JSON.parse(form.querySelector('[data-product-variants]')?.textContent || '[]');
    form.querySelectorAll('input[name^="options["]').forEach((input) => input.addEventListener('change', () => this.updateVariant()));
  }
  updateVariant() {
  const options = [...this.form.querySelectorAll('input[name^="options["]:checked')]
    .map((input) => input.value);

  const variant = this.variants.find((item) =>
    item.options.every((value, index) => value === options[index])
  );

  const idInput = this.form.querySelector('[data-variant-id]');
  const submit = this.form.querySelector('[type="submit"]');

  if (!variant) {
    submit.disabled = true;
    return;
  }

  idInput.value = variant.id;
  submit.disabled = !variant.available;

  // 更新商品价格
  const productSection = this.form.closest('.main-product-section');
  const priceElement = productSection?.querySelector('.price');

  if (priceElement) {
    const currency = window.Shopify?.currency?.active || 'USD';

    const formatPrice = (amount) =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
      }).format(amount / 100);

    if (variant.compare_at_price > variant.price) {
      priceElement.innerHTML =
        '<s>' + formatPrice(variant.compare_at_price) + '</s>' +
        '<span class="price__sale">' + formatPrice(variant.price) + '</span>';
    } else {
      priceElement.innerHTML =
        '<span>' + formatPrice(variant.price) + '</span>';
    }
  }
}
}

class ProductGallery {
  constructor(gallery) {
    this.gallery = gallery;
    this.refresh();
    gallery.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (thumb && gallery.contains(thumb)) this.select(this.thumbs.indexOf(thumb));
    });
    gallery.querySelector('[data-gallery-previous]')?.addEventListener('click', () => this.select(this.index - 1));
    gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.select(this.index + 1));
  }

  refresh() {
    this.image = this.gallery.querySelector('.product-gallery__main img, .home-product__main-media img');
    this.thumbs = [...this.gallery.querySelectorAll('[data-gallery-thumb]')];
    this.index = Math.max(0, this.thumbs.findIndex((thumb) => thumb.classList.contains('is-active')));
    this.gallery.querySelectorAll('[data-gallery-previous], [data-gallery-next]').forEach((arrow) => {
      arrow.hidden = this.thumbs.length < 2;
    });
  }

  select(index) {
    if (!this.image || !this.thumbs.length) return;
    this.index = (index + this.thumbs.length) % this.thumbs.length;
    const selected = this.thumbs[this.index];
    if (!selected.dataset.galleryImage) return;
    this.image.src = selected.dataset.galleryImage;
    this.image.srcset = '';
    this.image.removeAttribute('sizes');
    this.thumbs.forEach((thumb, itemIndex) => {
      const active = itemIndex === this.index;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', String(active));
    });
  }
}

class ProductZoom {
  constructor(container) {
    this.container = container;
    this.precisePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    if (!this.precisePointer.matches || !container.querySelector('img')) return;
    container.addEventListener('pointerenter', () => container.classList.add('is-zoomed'));
    container.addEventListener('pointermove', (event) => this.move(event));
    container.addEventListener('pointerleave', () => this.reset());
    container.addEventListener('focus', () => container.classList.add('is-zoomed'));
    container.addEventListener('blur', () => this.reset());
  }

  move(event) {
    const rect = this.container.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
    this.container.style.setProperty('--zoom-x', `${x}%`);
    this.container.style.setProperty('--zoom-y', `${y}%`);
  }

  reset() {
    this.container.classList.remove('is-zoomed');
    this.container.style.removeProperty('--zoom-x');
    this.container.style.removeProperty('--zoom-y');
  }
}

class HeroVideo {
  constructor(section) {
    this.videos = [...section.querySelectorAll('.hero__video')];
    this.button = section.querySelector('[data-video-toggle]');
    if (!this.button || !this.videos.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.videos.forEach((video) => video.pause());
      this.update(false);
    }
    this.button.addEventListener('click', () => this.toggle());
  }

  toggle() {
    const playing = this.videos.some((video) => !video.paused);
    if (playing) this.videos.forEach((video) => video.pause());
    else this.videos.forEach((video) => video.play().catch(() => {}));
    this.update(!playing);
  }

  update(playing) {
    const text = playing ? 'Pause' : 'Play';
    this.button.setAttribute('aria-label', `${text} video`);
    const label = this.button.querySelector('[data-video-toggle-label]');
    if (label) label.textContent = text;
  }
}

class FooterMenus {
  constructor() {
    this.mobile = window.matchMedia('(max-width: 749px)');
    document.querySelectorAll('.footer__menu-toggle').forEach((button) => {
      button.setAttribute('aria-expanded', String(!this.mobile.matches));
      button.addEventListener('click', () => {
        if (!this.mobile.matches) return;
        const menu = button.closest('.footer__menu');
        const open = !menu.classList.contains('is-open');
        menu.classList.toggle('is-open', open);
        button.setAttribute('aria-expanded', String(open));
        button.querySelector('[aria-hidden="true"]').textContent = open ? '−' : '+';
      });
    });
  }
}

function initializeTheme(root = document) {
  if (root === document) {
    new ThemeMenu();
    new FooterMenus();
  }
  root.querySelectorAll('.product-form').forEach((form) => {
    if (form.dataset.initialized) return;
    form.dataset.initialized = 'true';
    new ProductForm(form);
  });
  root.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    if (gallery.dataset.initialized) return;
    gallery.dataset.initialized = 'true';
    gallery.productGallery = new ProductGallery(gallery);
  });
  root.querySelectorAll('[data-product-zoom]').forEach((container) => {
    if (container.dataset.zoomInitialized) return;
    container.dataset.zoomInitialized = 'true';
    new ProductZoom(container);
  });
  root.querySelectorAll('.hero').forEach((section) => {
    if (section.dataset.videoInitialized) return;
    section.dataset.videoInitialized = 'true';
    new HeroVideo(section);
  });
  initializeProductSwitchers();
}

let spotlightSwitcher;
let fitSwitcher;
function initializeProductSwitchers() {
  const spotlight = document.querySelector('[data-product-spotlight]');
  const finder = document.querySelector('[data-fit-finder]');
  const spotlightChanged = spotlight !== spotlightSwitcher?.spotlight;
  if (spotlightChanged) {
    spotlightSwitcher?.destroy();
    spotlightSwitcher = spotlight ? new SpotlightProductSwitcher() : null;
  }
  if (finder !== fitSwitcher?.finder) {
    fitSwitcher = finder ? new FitProductSwitcher() : null;
  } else if (spotlightChanged && fitSwitcher) {
    fitSwitcher.select(fitSwitcher.finder.dataset.selectedHandle || fitSwitcher.cards[0]?.dataset.productHandle);
  }
}

document.addEventListener('DOMContentLoaded', () => initializeTheme());
document.addEventListener('shopify:section:load', (event) => initializeTheme(event.target));
